import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { FileUploadModule } from 'primeng/fileupload';
import { JobService } from '../../Utils/_services/job/job.service';
import { AppliedJobService } from '../../Utils/_services/applied-job/applied-job.service';
import { ToastrService } from 'ngx-toastr';
import { IJob } from '../../Utils/_services/job/job.model';
import { ICustomError } from '../../Utils/_models/network-response';

@Component({
  selector: 'app-careers',
  standalone: true,
  imports: [CommonModule, CardModule, DialogModule, FormsModule, FileUploadModule],
  templateUrl: './careers.component.html',
  styleUrls: ['./careers.component.css']
})
export class CareersComponent implements OnInit {


  public jobs: IJob[] = [];
  public allJobs: IJob[] = [];

  public currentPage: number = 1;
  public itemsPerPage: number = 6;
  public totalPages: number = 0;
  public totalJobs: number = 0;

  public isLoading: boolean = false;
  public isSubmitting: boolean = false;

  public selectedJob: IJob | null = null;
  public displayDialog: boolean = false;
  public application = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    coverLetter: "",
    resume: undefined as File | undefined
  };
  public files: { [key: string]: File } = {};
  public appService: any;

  public selectedLocation: string = "All Locations";
  public selectedType: string = "All Types";
  public selectedDepartment: string = "All Departments";
  public searchTerm: string = "";

  public locationOptions: string[] = ["All Locations", "Remote", "San Francisco, CA", "New York, NY"];
  public typeOptions: string[] = ["All Types", "Full-time", "Part-time", "Contract"];
  public departmentOptions: string[] = ["All Departments", "Engineering", "Design", "Marketing", "AI/ML"];

  public isDragActive: boolean = false;
  public resumeFile: File | null = null;

  constructor(
    private jobService: JobService,
    private appliedJob: AppliedJobService,
    private toastr: ToastrService
  ) { }


  ngOnInit(): void {
    this.loadJobs();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragActive = false;
  }

  onFileDrop(event: DragEvent, type: string): void {
    event.preventDefault();
    this.isDragActive = false;

    if (event.dataTransfer?.files.length) {
      const file = event.dataTransfer.files[0];
      this.setFile(file, type);
    }
  }

  onFileChange(event: Event, type: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.setFile(file, type);
    }
  }

  setFile(file: File, type: string): void {
    if (type === 'resume') {
      this.resumeFile = file;
      this.application.resume = file;
    }
  }

  loadJobs(): void {
    this.isLoading = true;

    this.jobService.getJobs(1, 10).subscribe({
      next: (response) => {
        const jobsData = response.data.items || response || [];
        this.allJobs = jobsData.map((job: any) => {
          console.log("Processing job:", job.jobTitle, "Requirements:", job.Requirements);
          return {
            ...job,
            title: job.title || job.jobTitle,
            type: job.type || job.jobType,
            posted: job.posted || job.created,
            requirements: job.requirements ? job.requirements.split(',').map((req: string) => req.trim()) : []
          };
        });

        this.totalJobs = this.allJobs.length;
        this.totalPages = Math.ceil(this.totalJobs / this.itemsPerPage);

        this.updatePaginatedJobs();
        this.isLoading = false;
      },
      error: (err: ICustomError) => {
        this.toastr.error('Failed to load jobs. Please try again.', err.error.msg);
        this.isLoading = false;
      }
    });
  }


  updatePaginatedJobs(): void {
    const filteredJobs = this.getFilteredJobs();
    this.totalPages = Math.ceil(filteredJobs.length / this.itemsPerPage);

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.jobs = filteredJobs.slice(startIndex, endIndex);
  }

  // Pagination methods
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePaginatedJobs();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedJobs();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedJobs();
    }
  }

  getPaginationArray(): number[] {
    const maxVisible = 5;
    const start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(this.totalPages, start + maxVisible - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  openApplyDialog(job: IJob) {
    this.selectedJob = job
    this.displayDialog = true
    this.application = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      coverLetter: "",
      resume: undefined
    }
  }

  closeDialog(): void {
    this.displayDialog = false;
    this.selectedJob = null;
    this.resetApplication();
    this.isSubmitting = false;
  }

  //   onFileChange(event: any, type: string) {
  //   const file = event.target.files?.[0]
  //   if (file) {
  //     this.files[type] = file

  //     console.log("File",file, this.files)
  //   } else {
  //     delete this.files[type]
  //   }
  // }


  submitApplication(): void {
    if (!this.application.firstName || !this.application.lastName || !this.application.email) {
      this.toastr.error("Please fill in all required fields.", "Validation Error");
      return;
    }

    if (!this.resumeFile) {
      this.toastr.error("Please upload your resume.", "Validation Error");
      return;
    }

    if (!this.selectedJob) {
      this.toastr.error("No job selected.", "Error");
      return;
    }

    this.isSubmitting = true;
    this.toastr.info("Submitting your application...", "Please Wait");

    const formData = new FormData();
    formData.append("JobId", this.selectedJob.jobId?.toString() || '');
    formData.append("Email", this.application.email);
    formData.append("PhoneNumber", this.application.phone || '');
    formData.append("CoverLetterPath", this.application.coverLetter || '');
    formData.append("EmployerName", this.application.firstName + ' ' + this.application.lastName || '');
    formData.append("ResumeFile", this.resumeFile);

    this.appliedJob.createAppliedJob(formData).subscribe({
      next: () => {
        this.toastr.success('Application submitted successfully!', 'Success');
        this.isSubmitting = false;
        this.closeDialog();
        this.resetApplication();
      },
      error: (err: ICustomError) => {
        console.error('Application submission failed', err.error.msg);
        const errorMsg = err?.error?.msg || err.message || 'Failed to submit application';
        this.toastr.error(errorMsg, 'Submission Failed');
        this.isSubmitting = false;
      }
    });
  }

  resetApplication(): void {
    this.application = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      coverLetter: "",
      resume: undefined
    };
    this.resumeFile = null;
  }

  // Filter methods (optional - for future enhancement)
  getFilteredJobs(): IJob[] {
    return this.allJobs.filter((job) => {
      const locationMatch = this.selectedLocation === "All Locations" || job.location === this.selectedLocation;
      const typeMatch = this.selectedType === "All Types" || (job.type || job.jobType) === this.selectedType;
      const departmentMatch =
        this.selectedDepartment === "All Departments" || job.department === this.selectedDepartment;
      const searchMatch =
        !this.searchTerm ||
        (job.title || job.jobTitle)?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        job.company.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(this.searchTerm.toLowerCase());

      return locationMatch && typeMatch && departmentMatch && searchMatch;
    });
  }

  // Utility methods
  getDaysRemaining(endDate: Date | string | undefined): number {
    if (!endDate) return 0;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  isJobExpiringSoon(endDate: Date | string | undefined): boolean {
    if (!endDate) return false;
    return this.getDaysRemaining(endDate) <= 7;
  }

  getJobTypeIcon(type: string): string {
    switch (type) {
      case "Full-time":
        return "💼";
      case "Part-time":
        return "⏰";
      case "Contract":
        return "📋";
      default:
        return "💼";
    }
  }

  clearFilters(): void {
    this.selectedLocation = "All Locations";
    this.selectedType = "All Types";
    this.selectedDepartment = "All Departments";
    this.searchTerm = "";
    this.currentPage = 1;
    this.updatePaginatedJobs();
  }

  // Method to trigger pagination update when filters change
  onFilterChange(): void {
    this.currentPage = 1;
    this.updatePaginatedJobs();
  }
}
